import { ArrowUpRight, LifeBuoy, ListChecks, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../App.js";
import { api } from "../lib/api.js";
import { lastVault, touchVault } from "../lib/recentVault.js";

export interface UnlockRequestView {
  id: string;
  vaultId: string;
  vaultName: string;
  requester: { userId: string; name: string };
  reason: "missing" | "evidence" | "other";
  details: string;
  policeReport: { number: string; agency: string; contact: string } | null;
  fromMs: number;
  toMs: number;
  windowCount: number;
  windows: { deviceId: string; windowId: string; epoch: string; key: string }[];
  status: "pending" | "approved" | "denied" | "revoked" | "expired";
  approvals: { confidantId: string; at: string; name: string }[];
  denials: { confidantId: string; at: string; reason: string | null; name: string }[];
  needed: number;
  createdAt: string;
  expiresAt: string;
  grantExpiresAt: string | null;
  me: { isRequester: boolean; canApprove: boolean; approved: boolean; denied: boolean };
}

function isoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function statusBadge(s: UnlockRequestView["status"]) {
  const cls = s === "approved" ? "ok" : s === "pending" ? "warn" : "bad";
  return <span className={`badge ${cls}`}>{s}</span>;
}

export function Unlock() {
  const { me } = useSession();
  const [requests, setRequests] = useState<UnlockRequestView[]>([]);
  const [vaultId, setVaultId] = useState("");
  const [from, setFrom] = useState(isoLocal(new Date(Date.now() - 6 * 3600_000)));
  const [to, setTo] = useState(isoLocal(new Date()));
  const [reason, setReason] = useState<"missing" | "evidence" | "other">("missing");
  const [details, setDetails] = useState("");
  const [report, setReport] = useState({ number: "", agency: "", contact: "" });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Kid mode has its own one-click "Decrypt recordings" flow on the vault page — no request or
  // approval needed there, so it doesn't belong in this multi-party request/approve flow.
  const nonKidVaults = me?.vaults.filter((v) => v.mode !== "kid") ?? [];

  const load = useCallback(async () => {
    if (!me) return;
    const all: UnlockRequestView[] = [];
    for (const v of nonKidVaults) {
      const r = await api<{ requests: UnlockRequestView[] }>(`/v1/vaults/${v.id}/unlock-requests`);
      all.push(...r.requests);
    }
    all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    setRequests(all);
    if (!vaultId && nonKidVaults[0]) {
      const recent = lastVault(me.user.id);
      const stillMine = recent && nonKidVaults.some((v) => v.id === recent);
      setVaultId(stillMine ? recent : nonKidVaults[0].id);
    }
  }, [me, vaultId]);
  useEffect(() => {
    void load();
  }, [load]);

  async function submit() {
    if (!me) return;
    setBusy(true);
    setErr(null);
    try {
      const body = {
        fromMs: new Date(from).getTime(),
        toMs: new Date(to).getTime(),
        reason,
        ...(details ? { details } : {}),
        ...(report.number ? { policeReport: report } : {}),
      };
      await api(`/v1/vaults/${vaultId}/unlock-requests`, { body });
      touchVault(me.user.id, vaultId);
      setDetails("");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!me) return null;
  if (nonKidVaults.length === 0) {
    return (
      <div>
        <h2 style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <LifeBuoy size={20} className="icon" />
          Emergency unlock
        </h2>
        <div className="card">
          <p className="muted">
            Your vaults are all kid mode, which doesn't need a request or approval — open{" "}
            <Link to="/">a vault</Link> and use its "Decrypt recordings" button instead.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <h2 style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <LifeBuoy size={20} className="icon" />
        Emergency unlock
      </h2>
      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Send size={18} className="icon" />
          Request access to recordings
        </h2>
        <p className="muted">
          Ask the confidants to open a specific time range. Each approves from their own device.
          When enough have, only those minutes can be decrypted, and only by you or the owner.
          Everyone is notified and every step is logged.
        </p>
        <div className="grid">
          <div>
            <label>Vault</label>
            <select value={vaultId} onChange={(e) => setVaultId(e.target.value)}>
              {nonKidVaults.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <label>Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
              <option value="missing">Person is missing</option>
              <option value="evidence">Evidence needed (innocence, incident)</option>
              <option value="other">Other</option>
            </select>
            <label>What happened?</label>
            <input
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Short description for the confidants"
            />
          </div>
          <div>
            <label>From</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            <label>To</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
            <label>Police report (optional, attached as evidence)</label>
            <input
              value={report.number}
              onChange={(e) => setReport({ ...report, number: e.target.value })}
              placeholder="Report number"
            />
            <div className="row" style={{ marginTop: 6 }}>
              <input
                value={report.agency}
                onChange={(e) => setReport({ ...report, agency: e.target.value })}
                placeholder="Agency"
              />
              <input
                value={report.contact}
                onChange={(e) => setReport({ ...report, contact: e.target.value })}
                placeholder="Officer / contact"
              />
            </div>
          </div>
        </div>
        {err && <p className="error">{err}</p>}
        <button
          className="primary"
          style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
          onClick={submit}
          disabled={busy || !vaultId}
        >
          <Send size={15} />
          Send request to confidants
        </button>
      </div>

      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ListChecks size={18} className="icon" />
          Requests
        </h2>
        {requests.length === 0 && <p className="muted">No requests yet.</p>}
        <table>
          <thead>
            <tr>
              <th>Vault</th>
              <th>Range</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Approvals</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td>{r.vaultName}</td>
                <td>
                  {new Date(r.fromMs).toLocaleString()} → {new Date(r.toMs).toLocaleString()}
                  <div className="muted">
                    {r.windowCount} window{r.windowCount === 1 ? "" : "s"} · by {r.requester.name}
                  </div>
                </td>
                <td>{r.reason}</td>
                <td>{statusBadge(r.status)}</td>
                <td>
                  {r.approvals.length} / {r.needed}
                  {r.me.canApprove && <div className="badge warn">needs you</div>}
                </td>
                <td>
                  <Link to={`/unlock/${r.id}`}>
                    <button
                      className={r.me.canApprove ? "primary" : ""}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <ArrowUpRight size={14} />
                      Open
                    </button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
